import React, { useState, useEffect } from 'react';
import { getData } from '../firebase';

const BRAND = '#0f6e56';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEAR = new Date().getFullYear();

export default function ProfitLoss({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [period, setPeriod] = useState('month');
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(YEAR);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [inv, exp] = await Promise.all([getData(user.uid, 'invoices'), getData(user.uid, 'expenses')]);
      setInvoices(inv); setExpenses(exp); setLoading(false);
    };
    load();
  }, [user.uid]);

  const filterDate = d => {
    if (!d) return false;
    const dt = new Date(d);
    if (period === 'month') return dt.getMonth() === month && dt.getFullYear() === year;
    if (period === 'year') return dt.getFullYear() === year;
    return true;
  };

  const salesInv = invoices.filter(i => i.type === 'sales' && i.status === 'paid' && filterDate(i.date));
  const purchaseInv = invoices.filter(i => i.type === 'purchase' && filterDate(i.date));
  const filtExp = expenses.filter(e => filterDate(e.date));

  const revenue = salesInv.reduce((s, i) => s + i.amount, 0);
  const cogs = purchaseInv.reduce((s, i) => s + i.amount, 0);
  const grossProfit = revenue - cogs;
  const totalOpEx = filtExp.reduce((s, e) => s + e.amount, 0);
  const netProfit = grossProfit - totalOpEx;
  const grossMargin = revenue > 0 ? ((grossProfit / revenue) * 100).toFixed(1) : '0.0';
  const netMargin = revenue > 0 ? ((netProfit / revenue) * 100).toFixed(1) : '0.0';

  const expByCategory = {};
  filtExp.forEach(e => { expByCategory[e.category] = (expByCategory[e.category] || 0) + e.amount; });

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const periodLabel = period === 'month' ? `${MONTHS[month]} ${year}` : period === 'year' ? `Year ${year}` : 'All time';

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Profit & Loss</h2>
        <span style={{ fontSize: 13, color: '#888' }}>Period: {periodLabel}</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['month', 'year', 'all'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} style={{ padding: '7px 16px', borderRadius: 20, border: `1px solid ${period === p ? BRAND : '#ddd'}`, background: period === p ? BRAND : '#fff', color: period === p ? '#fff' : '#555', cursor: 'pointer', fontSize: 13, fontWeight: period === p ? 600 : 400 }}>
            {p === 'month' ? 'Monthly' : p === 'year' ? 'Yearly' : 'All time'}
          </button>
        ))}
        {period === 'month' && MONTHS.map((m, i) => (
          <button key={m} onClick={() => setMonth(i)} style={{ padding: '7px 12px', borderRadius: 20, border: `1px solid ${month === i ? '#185fa5' : '#ddd'}`, background: month === i ? '#185fa5' : '#fff', color: month === i ? '#fff' : '#555', cursor: 'pointer', fontSize: 12 }}>{m}</button>
        ))}
        {(period === 'month' || period === 'year') && [YEAR, YEAR-1, YEAR-2].map(y => (
          <button key={y} onClick={() => setYear(y)} style={{ padding: '7px 12px', borderRadius: 20, border: `1px solid ${year === y ? '#534ab7' : '#ddd'}`, background: year === y ? '#534ab7' : '#fff', color: year === y ? '#fff' : '#555', cursor: 'pointer', fontSize: 12 }}>{y}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#e1f5ee', borderRadius: 12, padding: 16 }}><div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Gross Margin</div><div style={{ fontSize: 28, fontWeight: 700, color: BRAND }}>{grossMargin}%</div></div>
        <div style={{ background: '#e6f1fb', borderRadius: 12, padding: 16 }}><div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>Net Margin</div><div style={{ fontSize: 28, fontWeight: 700, color: '#185fa5' }}>{netMargin}%</div></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PLSection title="Revenue" color={BRAND} bg="#e1f5ee">
            <PLRow label={`Sales (${salesInv.length} invoices)`} value={fmt(revenue)} />
            <PLRow label="Total Revenue" value={fmt(revenue)} bold />
          </PLSection>
          <PLSection title="Cost of Goods Sold" color="#185fa5" bg="#e6f1fb">
            <PLRow label="Purchase invoices" value={fmt(cogs)} />
            <PLRow label="Total COGS" value={fmt(cogs)} bold />
          </PLSection>
          <div style={{ background: grossProfit >= 0 ? '#e1f5ee' : '#fcebeb', borderRadius: 12, padding: '14px 20px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: grossProfit >= 0 ? BRAND : '#a32d2d' }}>Gross Profit</span>
            <span style={{ fontWeight: 700, fontSize: 18, color: grossProfit >= 0 ? BRAND : '#a32d2d' }}>{fmt(grossProfit)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PLSection title="Operating Expenses" color="#854f0b" bg="#faeeda">
            {Object.entries(expByCategory).length === 0
              ? <div style={{ padding: '10px 16px', fontSize: 13, color: '#aaa' }}>No expenses in this period</div>
              : Object.entries(expByCategory).map(([cat, amt]) => <PLRow key={cat} label={cat} value={fmt(amt)} />)
            }
            <PLRow label="Total Expenses" value={fmt(totalOpEx)} bold />
          </PLSection>
          <div style={{ background: netProfit >= 0 ? '#e1f5ee' : '#fcebeb', borderRadius: 12, padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 16, color: netProfit >= 0 ? BRAND : '#a32d2d' }}>Net Profit</span>
            <span style={{ fontWeight: 700, fontSize: 24, color: netProfit >= 0 ? BRAND : '#a32d2d' }}>{fmt(netProfit)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PLSection({ title, color, bg, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
      <div style={{ background: bg, padding: '10px 16px', fontSize: 13, fontWeight: 700, color }}>{title}</div>
      {children}
    </div>
  );
}

function PLRow({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 16px', borderTop: '1px solid #f0f0f0' }}>
      <span style={{ fontSize: 13, color: bold ? '#222' : '#555', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: 13, color: bold ? '#222' : '#444', fontWeight: bold ? 700 : 400 }}>{value}</span>
    </div>
  );
}
